import * as React from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import {AdminGeneralInfoResult} from '../../../../models/adminGeneral';
import {formatRubles} from '../../../../common/money';
import {Delta} from '../Delta';
import {useNavigate} from 'react-router-dom';

export type DepositsCardProps = { data: AdminGeneralInfoResult | null };

export const DepositsCard: React.FC<DepositsCardProps> = React.memo(({data}) => {
    const navigate = useNavigate();
    return (
        <Sheet variant="outlined" sx={{
            p: 2,
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
        }}>
            <Box
                sx={{display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer'}}
                role="button"
                tabIndex={0}
                onClick={() => navigate('/app/payment-methods#stats-charts')}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate('/app/payment-methods#stats-charts');
                    }
                }}
            >
                <SavingsRoundedIcon fontSize="small"/>
                <Typography level="title-md">Депозиты</Typography>
            </Box>
            <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                <Box>
                    <Typography level="body-sm">Количество</Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.25, flexWrap: 'wrap'}}>
                        <Tooltip title="Количество депозитов за выбранный (прошлый) периоды" disablePortal={false}>
                            <Box sx={{display: 'inline-flex', alignItems: 'center', columnGap: 1}}>
                                <Typography level="title-lg"
                                            sx={{color: (data && (data.depositCount ?? 0) > (data.depositCountLastPeriod ?? 0)) ? 'success.600' : 'danger.600'}}>
                                    {data ? `+${data.depositCount ?? 0}` : '-'}
                                </Typography>
                                <Typography level="body-sm" sx={{color: 'neutral.500'}}>
                                    {data ? `(+${data.depositCountLastPeriod ?? 0})` : ''}
                                </Typography>
                            </Box>
                        </Tooltip>
                        {data ? (
                            <Tooltip title="Прирост относительно прошлого месяца" disablePortal={false}>
                                <Box sx={{ml: 0.5}}>
                                    <Delta current={data.depositCount ?? 0}
                                           previous={data.depositCountLastPeriod ?? 0}/>
                                </Box>
                            </Tooltip>
                        ) : null}
                    </Box>
                </Box>
                <Box>
                    <Typography level="body-sm">Сумма</Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.25, flexWrap: 'wrap'}}>
                        <Tooltip title="Сумма депозитов за выбранный (прошлый) периоды" disablePortal={false}>
                            <Box sx={{display: 'inline-flex', alignItems: 'center', columnGap: 1}}>
                                <Typography level="title-lg"
                                            sx={{color: (data && (data.depositSum ?? 0) > (data.depositSumLastPeriod ?? 0)) ? 'success.600' : 'danger.600'}}>
                                    {data ? `+${formatRubles(data.depositSum ?? 0)}` : '-'}
                                </Typography>
                                <Typography level="body-sm" sx={{color: 'neutral.500'}}>
                                    {data ? `(+${formatRubles(data.depositSumLastPeriod ?? 0)})` : ''}
                                </Typography>
                            </Box>
                        </Tooltip>
                        {data ? (
                            <Tooltip title="Прирост относительно прошлого месяца" disablePortal={false}>
                                <Box sx={{ml: 0.5}}>
                                    <Delta current={data.depositSum ?? 0} previous={data.depositSumLastPeriod ?? 0}/>
                                </Box>
                            </Tooltip>
                        ) : null}
                    </Box>
                </Box>
                <Box sx={{flexBasis: '100%', width: '100%', mt: 0.5}}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        columnGap: 1,
                        flexWrap: 'nowrap',
                        whiteSpace: 'nowrap',
                        width: '100%'
                    }}>
                        <Tooltip title="Профит по депозитам за выбранный (прошлый) периоды" disablePortal={false}>
                            <Box sx={{display: 'flex', alignItems: 'center', columnGap: 1}}>
                                <TrendingUpRoundedIcon fontSize="small"
                                                       sx={{color: (data && (data.depositProfit ?? 0) > (data.depositProfitLastPeriod ?? 0)) ? 'success.600' : 'danger.600'}}/>
                                <Typography noWrap level="title-lg"
                                            sx={{color: (data && (data.depositProfit ?? 0) > (data.depositProfitLastPeriod ?? 0)) ? 'success.600' : 'danger.600'}}>
                                    {data ? `+${formatRubles(data.depositProfit ?? 0)}` : '-'}
                                </Typography>
                                <Typography noWrap level="body-sm" sx={{color: 'neutral.500'}}>
                                    {data ? `(+${formatRubles(data.depositProfitLastPeriod ?? 0)})` : ''}
                                </Typography>
                            </Box>
                        </Tooltip>
                        {data ? (
                            <Tooltip title="Прирост относительно прошлого месяца" disablePortal={false}>
                                <Box sx={{ml: 0.5}}>
                                    <Delta current={data.depositProfit ?? 0}
                                           previous={data.depositProfitLastPeriod ?? 0}/>
                                </Box>
                            </Tooltip>
                        ) : null}
                    </Box>
                </Box>
                <Box>
                    <Typography level="body-sm">В день (кол-во)</Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.25, flexWrap: 'wrap'}}>
                        <Tooltip title="Среднее кол-во депозитов в день этого (прошлого) периодов"
                                 disablePortal={false}>
                            <Box sx={{display: 'inline-flex', alignItems: 'center', columnGap: 1}}>
                                <Typography level="title-lg"
                                            sx={{color: (data && (data.depositInDayCount ?? 0) > (data.depositInDayCountLastPeriod ?? 0)) ? 'success.600' : 'danger.600'}}>
                                    {data ? `+${data.depositInDayCount ?? 0}` : '-'}
                                </Typography>
                                <Typography level="body-sm" sx={{color: 'neutral.500'}}>
                                    {data ? `(+${data.depositInDayCountLastPeriod ?? 0})` : ''}
                                </Typography>
                            </Box>
                        </Tooltip>
                        {data ? (
                            <Tooltip title="Прирост относительно прошлого месяца" disablePortal={false}>
                                <Box sx={{ml: 0.5}}>
                                    <Delta current={data.depositInDayCount ?? 0}
                                           previous={data.depositInDayCountLastPeriod ?? 0}/>
                                </Box>
                            </Tooltip>
                        ) : null}
                    </Box>
                </Box>
                <Box>
                    <Typography level="body-sm">В день (сумма)</Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.25, flexWrap: 'wrap'}}>
                        <Tooltip title="Средняя сумма депозитов в день этого (прошлого) периодов" disablePortal={false}>
                            <Box sx={{display: 'inline-flex', alignItems: 'center', columnGap: 1}}>
                                <Typography level="title-lg"
                                            sx={{color: (data && (data.depositInDaySum ?? 0) > (data.depositInDaySumLastPeriod ?? 0)) ? 'success.600' : 'danger.600'}}>
                                    {data ? `+${formatRubles(data.depositInDaySum ?? 0)}` : '-'}
                                </Typography>
                                <Typography level="body-sm" sx={{color: 'neutral.500'}}>
                                    {data ? `(+${formatRubles(data.depositInDaySumLastPeriod ?? 0)})` : ''}
                                </Typography>
                            </Box>
                        </Tooltip>
                        {data ? (
                            <Tooltip title="Прирост относительно прошлого месяца" disablePortal={false}>
                                <Box sx={{ml: 0.5}}>
                                    <Delta current={data.depositInDaySum ?? 0}
                                           previous={data.depositInDaySumLastPeriod ?? 0}/>
                                </Box>
                            </Tooltip>
                        ) : null}
                    </Box>
                </Box>
            </Box>
        </Sheet>
    );
});

DepositsCard.displayName = 'DepositsCard';


